import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  AccommodationIcalExport,
  AccommodationIcalImport,
  DistributionChannel,
  SaveExportPayload,
  SaveImportPayload,
  fetchDistributionChannels,
  fetchIcalExports,
  fetchIcalImports,
  saveIcalExport,
  saveIcalImport,
  deleteIcalImport,
  deleteIcalExport,
  triggerIcalImport,
} from '../../common/api/icalService';

export interface IcalState {
  distributionChannels: DistributionChannel[];
  icalImports: AccommodationIcalImport[];
  icalExports: AccommodationIcalExport[];
  loadingStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  saveImportStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  saveExportStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  deleteImportStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  deleteExportStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  triggerImportStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: IcalState = {
  distributionChannels: [],
  icalImports: [],
  icalExports: [],
  loadingStatus: LOADING_STATUS.IDLE,
  saveImportStatus: LOADING_STATUS.IDLE,
  saveExportStatus: LOADING_STATUS.IDLE,
  deleteImportStatus: LOADING_STATUS.IDLE,
  deleteExportStatus: LOADING_STATUS.IDLE,
  triggerImportStatus: LOADING_STATUS.IDLE,
};

export const fetchIcalDataAsync = createAsyncThunk(
  'ical/fetchIcalData',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const [channels, imports, exports] = await Promise.all([
        fetchDistributionChannels(),
        fetchIcalImports(accommodationId),
        fetchIcalExports(accommodationId),
      ]);
      return {
        distributionChannels: channels,
        icalImports: imports,
        icalExports: exports,
      };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const saveIcalImportAsync = createAsyncThunk(
  'ical/saveIcalImport',
  async (payload: SaveImportPayload, { rejectWithValue }) => {
    try {
      const response = await saveIcalImport(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const saveIcalExportAsync = createAsyncThunk(
  'ical/saveIcalExport',
  async (payload: SaveExportPayload, { rejectWithValue }) => {
    try {
      const response = await saveIcalExport(payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const deleteIcalImportAsync = createAsyncThunk(
  'ical/deleteIcalImport',
  async (importId: string, { rejectWithValue }) => {
    try {
      await deleteIcalImport(importId);
      return importId;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const deleteIcalExportAsync = createAsyncThunk(
  'ical/deleteIcalExport',
  async (exportId: string, { rejectWithValue }) => {
    try {
      await deleteIcalExport(exportId);
      return exportId;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const triggerIcalImportAsync = createAsyncThunk(
  'ical/triggerIcalImport',
  async (
    {
      accommodationNumber,
      distributionChannelId,
    }: { accommodationNumber: number; distributionChannelId?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await triggerIcalImport(
        accommodationNumber,
        distributionChannelId,
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

const icalSlice = createSlice({
  name: 'ical',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchIcalDataAsync.pending, state => {
        state.loadingStatus = LOADING_STATUS.LOADING;
      })
      .addCase(fetchIcalDataAsync.fulfilled, (state, action) => {
        state.loadingStatus = LOADING_STATUS.IDLE;
        state.distributionChannels = action.payload.distributionChannels;
        state.icalImports = action.payload.icalImports;
        state.icalExports = action.payload.icalExports;
      })
      .addCase(fetchIcalDataAsync.rejected, state => {
        state.loadingStatus = LOADING_STATUS.FAILED;
      })
      .addCase(saveIcalImportAsync.pending, state => {
        state.saveImportStatus = LOADING_STATUS.LOADING;
      })
      .addCase(saveIcalImportAsync.fulfilled, state => {
        state.saveImportStatus = LOADING_STATUS.IDLE;
      })
      .addCase(saveIcalImportAsync.rejected, state => {
        state.saveImportStatus = LOADING_STATUS.FAILED;
      })
      .addCase(saveIcalExportAsync.pending, state => {
        state.saveExportStatus = LOADING_STATUS.LOADING;
      })
      .addCase(saveIcalExportAsync.fulfilled, state => {
        state.saveExportStatus = LOADING_STATUS.IDLE;
      })
      .addCase(saveIcalExportAsync.rejected, state => {
        state.saveExportStatus = LOADING_STATUS.FAILED;
      })
      .addCase(deleteIcalImportAsync.pending, state => {
        state.deleteImportStatus = LOADING_STATUS.LOADING;
      })
      .addCase(deleteIcalImportAsync.fulfilled, state => {
        state.deleteImportStatus = LOADING_STATUS.IDLE;
      })
      .addCase(deleteIcalImportAsync.rejected, state => {
        state.deleteImportStatus = LOADING_STATUS.FAILED;
      })
      .addCase(deleteIcalExportAsync.pending, state => {
        state.deleteExportStatus = LOADING_STATUS.LOADING;
      })
      .addCase(deleteIcalExportAsync.fulfilled, state => {
        state.deleteExportStatus = LOADING_STATUS.IDLE;
      })
      .addCase(deleteIcalExportAsync.rejected, state => {
        state.deleteExportStatus = LOADING_STATUS.FAILED;
      })
      .addCase(triggerIcalImportAsync.pending, state => {
        state.triggerImportStatus = LOADING_STATUS.LOADING;
      })
      .addCase(triggerIcalImportAsync.fulfilled, state => {
        state.triggerImportStatus = LOADING_STATUS.IDLE;
      })
      .addCase(triggerIcalImportAsync.rejected, state => {
        state.triggerImportStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectDistributionChannels = (state: RootState) =>
  state.ical.distributionChannels;
export const selectIcalImports = (state: RootState) => state.ical.icalImports;
export const selectIcalExports = (state: RootState) => state.ical.icalExports;
export const selectIcalLoadingStatus = (state: RootState) =>
  state.ical.loadingStatus;

export default icalSlice.reducer;
